import { Box, Divider, makeStyles } from '@material-ui/core';
import { App } from 'hydra-data-types';
import { FunctionComponent, useEffect } from 'react';
import drYapko from '../../../assets/images/connect-yapko-highres-whitebg.png';
import drGaryElkins from '../../../assets/images/drElkins.png';
import drSimoneLogo from '../../../assets/images/drPeters.png';
import clariaLogoWithName from '../../../assets/svgs/logos/claria-app-logo-with-name.svg';
import menoLogoWithName from '../../../assets/svgs/logos/meno-app-logo-with-name.svg';
import nervaLogoWithName from '../../../assets/svgs/logos/nerva-app-logo-with-name.svg';
import quitLogoWithName from '../../../assets/svgs/logos/quit-app-logo-with-name.svg';
import relioLogoWithName from '../../../assets/svgs/logos/relio-app-logo-with-name.svg';
import neuraLogo from '../../../assets/svgs/neuraLogo.svg';
import { PageContainer } from '../../../components/PageContainer';
import { ReferralInfoFooter } from '../../../components/ReferralInfoFooter';
import { TopBar } from '../../../components/TopBar';
import { CONNECT_SCREENS } from '../../../constants';
import { Analytics } from '../../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../../helpers/analytics/AnalyticsEvent';
import { colors } from '../../../theme/colors';
import { ProgramCard, ProgramCardProps } from './ProgramCard';

const programs: ProgramCardProps[] = [
  {
    appLogo: nervaLogoWithName,
    description:
      'Manage Irritable Bowel Syndrome through a 6-week gut-directed hypnotherapy program.',
    cardColor: colors.secondLightGreen,
    creatorLogo: drSimoneLogo,
    creatorInfo: 'Dr. Simone Peters, Monash University',
    cardActionLabel: 'Visit webpage',
    cardActionLink: 'https://www.nervahealth.com/clinicians',
    externalLink: true,
    app: App.Nerva,
  },
  {
    appLogo: relioLogoWithName,
    description: `Manage chronic low back pain through a 6-week clinical hypnosis and pain
		education program.`,
    cardColor: colors.blue,
    creatorLogo: neuraLogo,
    creatorInfo: 'Leading researchers at Neuroscience Research Australia',
    cardActionLabel: 'Visit webpage',
    cardActionLink: 'https://www.reliohealth.com/',
    externalLink: true,
    isSingleCreator: false,
    app: App.Pain,
  },
  {
    appLogo: quitLogoWithName,
    description: 'Stop smoking & manage withdrawals with an evidence-based hypnotherapy app.',
    cardColor: colors.lightPeach,
    creatorLogo: drGaryElkins,
    creatorInfo: 'Dr. Gary Elkins, Baylor University',
    cardActionLabel: 'Visit webpage',
    cardActionLink: 'https://www.mindsethealth.com/finito-for-clinicians?utm_source=connect',
    externalLink: true,
    app: App.Quit,
  },
  {
    appLogo: clariaLogoWithName,
    description: `Manage anxiety, depression and life's stresses 
		with Claria's 5 week hypnosis program`,
    cardColor: colors.lightYellow,
    creatorLogo: drYapko,
    creatorInfo: 'Dr. Michael Yapko, Psychologist',
    cardActionLabel: 'Visit webpage',
    cardActionLink: 'https://join.clariamentalhealth.com/clinicians?utm_source=connect',
    externalLink: true,
    app: App.Quit,
  },
  {
    appLogo: menoLogoWithName,
    description: 'Manage hot flashes/flushes naturally during menopause with Evia hypnotherapy.',
    cardColor: colors.lightBlue,
    creatorLogo: drGaryElkins,
    creatorInfo: 'Dr. Gary Elkins, Baylor University',
    cardActionLabel: 'Visit webpage',
    cardActionLink: 'https://www.mindsethealth.com/evia-for-clinicians?utm_source=connect',
    externalLink: true,
    app: App.Meno,
  },
];

type OneOrTwoProgramCards = [ProgramCardProps] | [ProgramCardProps, ProgramCardProps];

/**
 * We create the programPairs to group the programs in pairs of 2.
 * This was the 4 tiles will display as 1 column of 4 rows, 2 rows of 2, or one row of 4.
 * This looks better than have three tiles on one row, and one tile on the next.
 */

const programPairs: Array<OneOrTwoProgramCards> = programs.reduce(
  (acc: Array<OneOrTwoProgramCards>, curr, index) => {
    // If the index is even, push a new sub-array with the current element.
    if (index % 2 === 0) acc.push([curr]);
    // If the index is odd, push the current element into the last sub-array.
    else acc[acc.length - 1].push(curr);

    return acc;
  },
  []
);

export const Programs: FunctionComponent = () => {
  const classes = useStyles();

  useEffect(function trackAnalytics() {
    Analytics.track({
      event: AnalyticsEventName.h_screen_viewed,
      screen: CONNECT_SCREENS.PROGRAMS,
    });
  }, []);

  return (
    <PageContainer testId="programs-screen">
      <TopBar
        title="Programs"
        description={`As a healthcare provider, you have complimentary access to all Mindset Health programs.
			Simply download the programs you're interested in from the App Store or Play Store.
			Sign in with the same email address and password you've used for Connect.`}
      />

      <Box className={classes.cards} flexGrow={0}>
        {programPairs.map((pair, index) => {
          return (
            <Box className={classes.programCardItem} key={index}>
              {pair.map((program, index) => {
                return <ProgramCard key={index} {...program} />;
              })}
            </Box>
          );
        })}
      </Box>
      <Divider />
      <ReferralInfoFooter />
    </PageContainer>
  );
};

const useStyles = makeStyles((theme) => ({
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    maxWidth: '750px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    marginBottom: theme.spacing(3),
  },
  programCardItem: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flexBasis: '50%',
  },
}));
